import { Badge, Group, Stack, Text } from "@mantine/core";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { DataTable, PageHeader } from "@how/ui";
import { CrmTabs } from "../CrmTabs";
import { useDuplicates, type DuplicatePair, type DuplicatePerson } from "./duplicate-queries";
import { MergeModal } from "./MergeModal";
import { describeReasons } from "./merge";

function PersonCell({ person }: { readonly person: DuplicatePerson }) {
  return (
    <Stack gap={0}>
      <Text size="sm" fw={600}>
        {person.firstName} {person.lastName}
      </Text>
      <Text size="xs" c="dimmed">
        {person.email ?? person.phone ?? "No contact details"}
      </Text>
    </Stack>
  );
}

const columns: ColumnDef<DuplicatePair>[] = [
  {
    id: "a",
    header: "Person",
    enableSorting: false,
    cell: (ctx) => <PersonCell person={ctx.row.original.a} />,
  },
  {
    id: "b",
    header: "Possible duplicate",
    enableSorting: false,
    cell: (ctx) => <PersonCell person={ctx.row.original.b} />,
  },
  {
    id: "reasons",
    header: "Why",
    enableSorting: false,
    cell: (ctx) => (
      <Group gap={6}>
        {describeReasons(ctx.row.original.reasons).map((reason) => (
          <Badge key={reason} variant="light" color="orange" size="md">
            {reason}
          </Badge>
        ))}
      </Group>
    ),
  },
];

const PAGE = { pageIndex: 0, pageSize: 200 };

/**
 * Likely duplicates, strongest match first. A row opens the two records side by side, where staff merge them or say
 * they are different people. The list is computed on request rather than stored, so it is always current.
 */
export function DuplicatesPage() {
  const duplicates = useDuplicates();
  const [open, setOpen] = useState<DuplicatePair | null>(null);
  const status = duplicates.isPending ? "loading" : duplicates.isError ? "error" : "ready";

  return (
    <>
      <PageHeader
        title="People"
        description="Everyone your church knows, whether or not they have a login."
      />
      <CrmTabs active="duplicates" />
      <DataTable
        label="Possible duplicates"
        columns={columns}
        rows={duplicates.data ?? []}
        totalRows={duplicates.data?.length ?? 0}
        status={status}
        pagination={PAGE}
        onPaginationChange={() => undefined}
        sorting={[]}
        onSortingChange={() => undefined}
        getRowId={(row) => row.key}
        onRowClick={(row) => {
          setOpen(row);
        }}
        emptyTitle="No likely duplicates"
        emptyDescription="People who share an email, a phone number, or a name and birthdate will show here."
        errorMessage="Could not look for duplicates."
        onRetry={() => {
          void duplicates.refetch();
        }}
      />
      {open === null ? null : (
        <MergeModal
          key={open.key}
          pair={open}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </>
  );
}
